import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Shield, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RolesTable } from "@/components/roles/RolesTable";
import { RoleModal } from "@/components/roles/RoleModal";
import { RoleFilters } from "@/components/roles/RoleFilters";
import { RoleCard } from "@/components/roles/RoleCard";
import { rolesService } from "@/services/roles";
import { Role } from "@/types/roles";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

export function RolesManagement() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
  const [statistics, setStatistics] = useState({
    totalRoles: 0,
    activeRoles: 0,
    totalUsers: 0,
    systemRoles: 0,
    customRoles: 0,
  });

  const { toast } = useToast();
  const { user, company, canManageRoles: canManageRolesFn } = useAuth();

  const companySlug = company?.slug || "";
  const companyId = company?.id || "";
  const canManageRoles = canManageRolesFn ? canManageRolesFn() : false;

  const loadRoles = async () => {
    if (!companySlug) return;
    try {
      setLoading(true);
      const { data } = await rolesService.getRoles(companySlug);
      setRoles(data);

      const systemRolesCount = data.filter((r) => r.is_system_role).length;
      setStatistics({
        totalRoles: data.length,
        activeRoles: data.filter((r) => r.is_active !== false).length,
        totalUsers: data.reduce((sum, r) => sum + (r.users_count || 0), 0),
        systemRoles: systemRolesCount,
        customRoles: data.length - systemRolesCount,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "No se pudieron cargar los roles",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companySlug) loadRoles();
  }, [companySlug]);

  const filteredRoles = roles.filter((role) => {
    const matchesSearch =
      role.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (role.description || "")
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const handleCreateRole = async (roleData: Partial<Role>) => {
    if (!companySlug || !companyId) return;
    try {
      setSaving(true);
      if (!user?.id) throw new Error("Usuario no identificado");
      const createData = {
        company_id: companyId,
        slug: companySlug,
        name: roleData.name || "",
        description: roleData.description || "",
        is_system_role: false,
        permissions: (roleData.permissions as any) || [],
        userId: user.id,
      };
      await rolesService.createRole(createData);
      toast({ title: "Éxito", description: "Rol creado correctamente" });
      await loadRoles();
      setIsModalOpen(false);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "No se pudo crear el rol",
        variant: "destructive",
      });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRole = async (roleId: string, roleData: Partial<Role>) => {
    try {
      setSaving(true);
      if (!user?.id) throw new Error("Usuario no identificado");
      await rolesService.updateRole(roleId, {
        slug: companySlug,
        userId: user.id,
        name: roleData.name || "",
        description: roleData.description || "",
        permissions: (roleData.permissions as any) || [],
      });
      toast({ title: "Éxito", description: "Rol actualizado correctamente" });
      await loadRoles();
      setIsModalOpen(false);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "No se pudo actualizar el rol",
        variant: "destructive",
      });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!roleToDelete || !user?.id) return;
    try {
      await rolesService.deleteRole(roleToDelete.id, companySlug, user.id);
      toast({ title: "Éxito", description: "Rol eliminado correctamente" });
      await loadRoles();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "No se pudo eliminar el rol",
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex gap-3">
          {canManageRoles && (
            <Button
              onClick={() => {
                setEditingRole(null);
                setIsModalOpen(true);
              }}
              className="bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-500/20"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Rol
            </Button>
          )}
        </div>
      </div>

      <RoleFilters
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        statistics={statistics}
      />

      {loading ? (
        <div className="space-y-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-16 bg-slate-50/50 animate-pulse rounded-lg border border-slate-100" />
          ))}
        </div>
      ) : (
        <div className="hidden md:block">
          <RolesTable
            roles={filteredRoles}
            onEdit={(role) => { setEditingRole(role); setIsModalOpen(true); }}
            onDelete={(role) => { setRoleToDelete(role); setDeleteDialogOpen(true); }}
            canManageRoles={canManageRoles}
          />
        </div>
      )}

      {canManageRoles && (
        <RoleModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setEditingRole(null);
          }}
          onSave={editingRole ? (data) => handleUpdateRole(editingRole.id, data) : handleCreateRole}
          role={editingRole}
          companySlug={companySlug}
          companyId={companyId}
          loading={saving}
        />
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar rol?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se revocará el acceso a los usuarios vinculados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
